/**
 * E5.2: which drawn tree stands at a flora entry, so what blocks follows what is drawn. The stands (rendering/
 * treeStands.ts) draw each normal tree as one of a few seeded variants of its species, turned by a yaw about its up
 * and, once felled, its trunk lying along a fall direction — all hashed from its id, never the sim's random streams.
 * Those draws live here so the sim (core/surfaceWalk.ts's tree pieces) and the drawing read the same numbers; and
 * the model's frame on the ground is the drawing's (a model's +y along the tree's up, by the shortest turn, then the yaw
 * about it). Pure; integer hashing and dmath only.
 */
import type { Vec3 } from '../types';
import { dsin, dcos, datan2, dtan } from './dmath';
import { FAMILY_VARIANTS, TREE_VARY } from '../constants/treeVary';

/** How many seeded variants of each tree the stands draw (rendering/treeStands.ts TREE_LOD.variants). */
export const TREE_VARIANTS = 3;

/** A deterministic hash of (n, salt) in [0, 1): the stands' per-prop draws (variant, yaw, fall). */
export function propHash(n: number, salt: number): number {
  let h = (Math.imul(n | 0, 0x9e3779b1) ^ Math.imul(salt, 0x85ebca77)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** How many prototypes the stands draw of a tree type: a family's FAMILY_VARIANTS (403), any other's TREE_VARIANTS. */
export function treeVariantCount(type: string): number { return TREE_VARY[type] ? FAMILY_VARIANTS : TREE_VARIANTS; }
/** The drawn variant of a tree (0 … treeVariantCount(type) − 1). */
export function treeVariant(id: number, type: string): number { return Math.floor(propHash(id, 1) * treeVariantCount(type)); }
/**
 * 403: what a family's variant `v` is built from — its seed's index (rendering/treeStands.ts treeProtoSeed) and its count's
 * step (rendering/treeFamilies.ts buildFamilyTree): two seeds of each of three counts; a family with no count, six seeds;
 * any other type, its own seed at no step.
 */
export function treeProtoOf(type: string, v: number): { seedIndex: number; steps: number } {
  const steps = TREE_VARY[type]?.steps;
  return steps?.length ? { seedIndex: Math.floor(v / steps.length), steps: steps[v % steps.length] } : { seedIndex: v, steps: 0 };
}

/**
 * 403: a tree's own shape inside its family (constants/treeVary.ts), hashed from its id (salts 45–49; 393's sheet's): the
 * trunk's height below the crown (×), the crown's width and height about its middle (×), and its lean — the bow's tangent
 * at the top and its heading in the model's frame. Null for a type with no row (it stands as its prototype).
 */
export interface TreeShapeDraw { trunk: number; crownW: number; crownH: number; lean: number; cos: number; sin: number }
export function treeShapeDraw(type: string, id: number): TreeShapeDraw | null {
  const v = TREE_VARY[type];
  if (!v) return null;
  const u = (salt: number) => propHash(id, salt), lerp = (r: readonly [number, number], t: number) => r[0] + (r[1] - r[0]) * t;
  const heading = u(49) * Math.PI * 2, l = u(48);
  return { trunk: lerp(v.trunk, u(45)), crownW: lerp(v.crownW, u(46)), crownH: lerp(v.crownH, u(47)), lean: dtan(v.lean * l * l * Math.PI / 180), cos: dcos(heading), sin: dsin(heading) };
}
/** 403: a tree's drawn colour inside its family — the leaves' hue turned (radians), their lightness and the bark's (±). Drawing only. */
export function treeTintDraw(type: string, id: number): [number, number, number] {
  const v = TREE_VARY[type];
  if (!v) return [0, 0, 0];
  return [v.hue[0] + (v.hue[1] - v.hue[0]) * propHash(id, 50), (2 * propHash(id, 51) - 1) * v.value, (2 * propHash(id, 52) - 1) * v.bark];
}

/**
 * 403: a model-space point (x, y up the trunk, z) of a prototype whose crown starts at `crown[0]` and is middled at
 * `crown[1]` (constants/treeWood.ts), under a tree's shape draw — the stands' vertex shader's rule (rendering/
 * treeStands.ts TREE_SHAPE_GLSL), which the sim's wood and crowns read: the crown blends in about its foot (a smoothstep
 * over 0.35 of its foot-to-middle on either side) and scales about its middle; below it the trunk stretches (the crown
 * rides up on it); then the whole bows by the lean, from a vertical foot. `out[3]` is the crown's weight there (the
 * radii's scale reads it). `lying` (a fallen form's [oy, oz]): the point is in the lying frame (rendering/treeForms.ts:
 * the model turned top along +z and moved by (0, oy, oz)) and bows only sideways.
 */
export function treeShapePoint(d: TreeShapeDraw, crown: readonly [number, number], x: number, y: number, z: number, lying?: readonly [number, number], out: number[] = [0, 0, 0, 0]): number[] {
  let X = x, Y = y, Z = z;
  if (lying) { Y = z - lying[1]; Z = lying[0] - y; }
  const cb = crown[0], cc = crown[1], span = Math.max(1e-4, cc - cb);
  const t = Math.min(1, Math.max(0, (Y - (cb - 0.35 * span)) / (0.7 * span))), w = t * t * (3 - 2 * t);
  const lift = Math.min(Y, cb) * (d.trunk - 1);
  X += (X * d.crownW - X) * w; Z += (Z * d.crownW - Z) * w; Y += (cc + (Y - cc) * d.crownH - Y) * w;
  Y += lift;
  const yy = Math.max(Y, 0), bow = d.lean * yy * yy / (yy + 0.15 * cc * d.trunk);
  X += bow * d.cos;
  if (!lying) Z += bow * d.sin;
  if (lying) { out[0] = X; out[1] = lying[0] - Z; out[2] = Y + lying[1]; }
  else { out[0] = X; out[1] = Y; out[2] = Z; }
  out[3] = w;
  return out;
}
/** The drawn yaw of a standing tree about its up (radians). */
export function treeYaw(id: number): number { return propHash(id, 2) * Math.PI * 2; }
/** The drawn variant of a normal (biome) rock (0 … TREE_VARIANTS − 1): its prototype's (66.9: the hull the cover slots read). */
export function rockVariant(id: number): number { return Math.floor(propHash(id, 3) * TREE_VARIANTS); }
/** The drawn yaw of a normal rock about its up (radians): the turn of its hull on the ground (66.9). */
export function rockYaw(id: number): number { return propHash(id, 4) * Math.PI * 2; }

/**
 * Model space (x right, y up the tree, z) to world directions for a tree standing along `up` (unit) turned by `yaw`:
 * the yaw about the model's y, then the shortest turn of +y onto `up` (as THREE's setFromUnitVectors, which the
 * stands use; straight down turns about x).
 */
export function treeFrame(up: Vec3, yaw: number): (x: number, y: number, z: number) => Vec3 {
  const cy = dcos(yaw), sy = dsin(yaw);
  const c = up.y;
  return (x0, y, z0) => {
    const x = x0 * cy + z0 * sy, z = -x0 * sy + z0 * cy;
    if (c < -1 + 1e-9) return { x, y: -y, z: -z };
    // R v = v + w × v + w × (w × v) / (1 + c), w = (0,1,0) × up = (up.z, 0, −up.x)
    const wx = up.z, wz = -up.x;
    const cx = -wz * y, cyy = wz * x - wx * z, cz = wx * y;          // w × v
    const k = 1 / (1 + c);
    const ccx = -wz * cyy, ccy = wz * cx - wx * cz, ccz = wx * cyy;   // w × (w × v)
    return { x: x + cx + ccx * k, y: y + cyy + ccy * k, z: z + cz + ccz * k };
  };
}

/**
 * The fall of a felled tree, in its model frame's yaw (0 = along +z): away from where the blow came from (`from`,
 * world) seen from its foot (`foot`, world) on the ground plane of `up`, or a hashed way when nothing says (as the
 * stands draw it).
 */
export function treeFallYaw(id: number, up: Vec3, foot: Vec3, from: Vec3 | null | undefined): number {
  if (from) {
    const px = foot.x - from.x, py = foot.y - from.y, pz = foot.z - from.z;
    // into the model frame with no yaw: the inverse (transpose) of the shortest turn
    const toWorld = treeFrame(up, 0);
    const ex = toWorld(1, 0, 0), ez = toWorld(0, 0, 1);
    const lx = px * ex.x + py * ex.y + pz * ex.z, lz = px * ez.x + py * ez.y + pz * ez.z;
    if (lx * lx + lz * lz > 1e-18) return datan2(lx, lz);
  }
  return propHash(id, 5) * Math.PI * 2;
}
